/**
 * Guidance for a session started from "Explain error" on a failed GitHub
 * Actions job, rendered as an additive `runtime_instructions` contribution to
 * the host's system prompt.
 *
 * The turn arrives as a canned prompt plus a `github_actions_job` attachment,
 * and nothing tells the agent what the task is. Left alone it rediscovers the
 * run and job the attachment already identifies, reads a raw log tail, and
 * keeps digging through commit history after the cause is known. The previous
 * actions agent avoided that implicitly through a two-tool scope, a low
 * tool-loop cap, and its skill descriptions; this states the same scope.
 *
 * `runtime_instructions` is the host's section for client-specific tool
 * guidance, which is what task-scoped direction for one entry point is.
 */
import type {SystemMessageSections} from './system-message-sections'

/** Section id the host accepts for situational, tool-specific guidance. */
const RUNTIME_INSTRUCTIONS_SECTION = 'runtime_instructions'

export const EXPLAIN_ERROR_CONTENT = [
  '## Explaining a failed GitHub Actions job',
  'This session was started from "Explain error" on the failed job in the attached `github_actions_job`. Its URL contains the run ID and job ID.',
  "- Begin with two parallel calls: `get_job_logs` for that `job_id` with `tail_lines: 150`, and `actions_get` `get_workflow_run` for the run, which gives the `head_sha` and the workflow file `path`. If the failure isn't in those log lines, fetch 5000 lines once.",
  "- Then, in parallel, read the workflow file and only the files the failing lines point to, using `get_file_contents` at `head_sha`. Don't clone, curl, or search the repository.",
  "- Work only from the log and the files you read: don't browse the web, edit files, run code, commit, push, or open pull requests. If you're unsure of the fix, say so.",
  '- Respond with the root cause (quote the key error line), a concrete fix with a code suggestion, and a link to the failing line. Write every file reference as a link: `https://github.com/<owner>/<repo>/blob/<head_sha>/<path>#L<line>`. Leave out speculative extras.',
].join('\n')

/**
 * Build the Explain error contribution for a session started from that entry
 * point, or `undefined` for any other session.
 */
export function buildExplainErrorSections(explainError: boolean | undefined): SystemMessageSections | undefined {
  if (!explainError) return undefined
  return {
    [RUNTIME_INSTRUCTIONS_SECTION]: {action: 'append', content: EXPLAIN_ERROR_CONTENT},
  }
}
